import {
    http, HttpResponse, bypass, passthrough
} from 'msw';

type MetricsParams = never;
type MetricsRequestBody = never;
type MetricsResponseBody = Record<string, unknown>;
// Wildcard at start to match http for local dev environment
const workstreamMetricsTpmPath = '*/performance-metrics/tpm/workstream';
type WorkstreamMetricsTpmPath = typeof workstreamMetricsTpmPath;
const interactionMetricsTpmPath = '*/performance-metrics/tpm/interaction';
type InteractionMetricsTpmPath = typeof interactionMetricsTpmPath;
 
export const handlers = [
    // Mock organisation API endpoints
    http.get('*/organisation/users/me', () => {
        return HttpResponse.json({
            id: 1,
            email: 'dev@test.com',
            firstName: 'Dev',
            lastName: 'User',
            permissions: ['*'],
            roles: ['admin']
        });
    }),
    http.get('*/organisation/organisations/me', () => {
        return HttpResponse.json({
            id: 1,
            name: 'Test Organisation',
            timezone: 'America/New_York'
        });
    }),
    http.get('*/organisation/system-roles', () => {
        return HttpResponse.json([
            { id: 1, name: 'admin', permissions: ['*'] }
        ]);
    }),
    http.get('*/organisation/page-preferences/me/global', () => {
        return HttpResponse.json({
            timeFormat: '12h' // Change this to '24h' to test 24-hour format
        });
    }),
    // Mock FeatureFlow API for feature flags
    http.get('*/organisation/feature-flags', () => {
        return HttpResponse.json({
            '2025q4-pvdev-37-teamschedule-enhanced': { enabled: true },
            'alph-3909-facet-visibility': { enabled: true }
        });
    }),
    http.get('*/api/organisation/env.js', () => {
        const envJs = `
            window.environmentData = {
                apiGatewayBaseUrl: 'http://localhost',
                organisationId: 1
            };
        `;
        return new HttpResponse(envJs, {
            headers: {
                'Content-Type': 'application/javascript'
            }
        });
    }),
    // Mock workactivity API - workstreams search
    http.get('*/workactivity/workstreams/search', () => {
        return HttpResponse.json({
            data: [
                {
                    id: '1',
                    workstreamId: 'WS-001',
                    customerId: 'CUST-001',
                    customerName: 'John Doe',
                    startTime: '2024-11-01T10:00:00Z',
                    closedTime: null,
                    assignedUserId: 1,
                    assignedUserName: 'Agent Smith',
                    teamId: 1,
                    teamName: 'Support Team',
                    workstreamDefinitionId: 1,
                    workstreamDefinitionName: 'Customer Support',
                    channelType: 'email'
                },
                {
                    id: '2',
                    workstreamId: 'WS-002',
                    customerId: 'CUST-002',
                    customerName: 'Jane Smith',
                    startTime: '2024-11-01T11:00:00Z',
                    closedTime: '2024-11-01T12:00:00Z',
                    assignedUserId: 2,
                    assignedUserName: 'Agent Johnson',
                    teamId: 1,
                    teamName: 'Support Team',
                    workstreamDefinitionId: 1,
                    workstreamDefinitionName: 'Customer Support',
                    channelType: 'chat'
                }
            ],
            itemsCount: 2,
            pageLimit: 20
        });
    }),
    // Mock workactivity API - interactions search
    http.get('*/workactivity/interactions/search', () => {
        return HttpResponse.json({
            data: [
                {
                    id: '1',
                    interactionId: 'INT-001',
                    workstreamId: 'WS-001',
                    startTime: '2024-11-01T10:00:00Z',
                    endTime: '2024-11-01T10:30:00Z',
                    userId: 1,
                    userName: 'Agent Smith',
                    channelType: 'email',
                    direction: 'inbound'
                },
                {
                    id: '2',
                    interactionId: 'INT-002',
                    workstreamId: 'WS-001',
                    startTime: '2024-11-01T10:30:00Z',
                    endTime: '2024-11-01T11:00:00Z',
                    userId: 1,
                    userName: 'Agent Smith',
                    channelType: 'email',
                    direction: 'outbound'
                }
            ],
            itemsCount: 2,
            pageLimit: 20
        });
    }),
    http.get<
	MetricsParams,
	MetricsRequestBody,
	MetricsResponseBody,
	WorkstreamMetricsTpmPath
	>
	(workstreamMetricsTpmPath, async ({ request }) => {
	    try {
	        // Make a call to interactions, we will update that response to mock workstreams
	        const { headers, url } = request;
	        if (url.includes('interval')) {
	            return passthrough();
	        }
	        const bypassUrl = new URL(url);
	        bypassUrl.searchParams.set('interval', 'day');

	        // Construct a proxy request based on the intercepted request
	        // but provide a new URL that contains modified query parameters.
	        const proxyRequest = new Request(bypassUrl, request);
	        const metricsResponse = await fetch(bypass(proxyRequest), {
	            method: 'GET',
	            headers: {
	                'Content-Type': 'application/json',
	                // Include any necessary headers from the initial request
	                ...Object.fromEntries(headers)
	            }
	        });
	        const metrics = await metricsResponse.json();

	        // The JSON response body type must satisfy
	        // the "AddCommentResponseBody" type.
	        return HttpResponse.json({ ...metrics });
	    }
	    catch (e) {
	        console.error('MSW Error', e);
	        return HttpResponse.json({ error: 'An error occurred' }, { status: 500 });
	    }
	}),
    http.get<
		MetricsParams,
		MetricsRequestBody,
		MetricsResponseBody,
		InteractionMetricsTpmPath
	>
	(interactionMetricsTpmPath, async ({ request }) => {
	    try {
	        // Make a call to interactions, we will update that response to mock workstreams
	        const { headers, url } = request;
	        if (url.includes('interval')) {
	            return passthrough();
	        }
	        const bypassUrl = new URL(url);
	        bypassUrl.searchParams.set('interval', 'day');

	        // Construct a proxy request based on the intercepted request
	        // but provide a new URL that contains modified query parameters.
	        const proxyRequest = new Request(bypassUrl, request);
	        const metricsResponse = await fetch(bypass(proxyRequest), {
	            method: 'GET',
	            headers: {
	                'Content-Type': 'application/json',
	                // Include any necessary headers from the initial request
	                ...Object.fromEntries(headers)
	            }
	        });
	        const metrics = await metricsResponse.json();

	        // The JSON response body type must satisfy
	        // the "AddCommentResponseBody" type.
	        return HttpResponse.json({ ...metrics });
	    }
	    catch (e) {
	        console.error('MSW Error', e);
	        return HttpResponse.json({ error: 'An error occurred' }, { status: 500 });
	    }
	})
];